export interface StoryMasteryChoice { readonly id: string; readonly text: string; }
export interface StoryMasteryTrial {
  readonly id: string;
  readonly skillId: string;
  readonly focus: 'sequence' | 'vocabulary' | 'character' | 'main-idea' | 'grammar' | 'fluency';
  readonly context: string;
  readonly prompt: string;
  readonly choices: readonly StoryMasteryChoice[];
  readonly correctChoiceId: string;
  readonly evidence: string;
  readonly hints: readonly string[];
}

export const GREAT_STORY_TREE_RESTORATION_EVENT = 'great-story-tree-fully-restored';

export const GREAT_STORY_TREE_TRIALS: readonly StoryMasteryTrial[] = [
  {
    id: 'root-sequence', skillId: 'reading-mastery-sequence', focus: 'sequence',
    context: 'Lena tucked a seed into soft soil. She watered it. The next week, a green shoot appeared.',
    prompt: 'Which event happened first?',
    choices: [{ id: 'water', text: 'Lena watered the soil.' }, { id: 'plant', text: 'Lena tucked in the seed.' }, { id: 'shoot', text: 'A green shoot appeared.' }],
    correctChoiceId: 'plant', evidence: 'The passage first says Lena tucked a seed into soil.',
    hints: ['Look at the first sentence.', 'Find what Lena did before she watered.', 'The seed had to go into the soil first.'],
  },
  {
    id: 'root-vocabulary', skillId: 'reading-mastery-vocabulary', focus: 'vocabulary',
    context: 'The path was dim, so I held my lantern close to see the stones.',
    prompt: 'What does “dim” mean here?',
    choices: [{ id: 'quiet', text: 'very quiet' }, { id: 'low-light', text: 'not very bright' }, { id: 'wide', text: 'very wide' }],
    correctChoiceId: 'low-light', evidence: 'The lantern helps the reader see because the path is not bright.',
    hints: ['Use the words after “so.”', 'Why would a lantern help someone see?', 'Dim means there is not much light.'],
  },
  {
    id: 'branch-character', skillId: 'reading-mastery-character', focus: 'character',
    context: 'Omar saw Nia drop her basket. He carried one handle while she carried the other.',
    prompt: 'Which trait best describes Omar?',
    choices: [{ id: 'helpful', text: 'helpful' }, { id: 'forgetful', text: 'forgetful' }, { id: 'sleepy', text: 'sleepy' }],
    correctChoiceId: 'helpful', evidence: 'Omar helps Nia carry the dropped basket.',
    hints: ['Notice what Omar does after the basket drops.', 'He shares the work with Nia.', 'Helping someone carry shows that Omar is helpful.'],
  },
  {
    id: 'branch-main-idea', skillId: 'reading-mastery-main-idea', focus: 'main-idea',
    context: 'Ants find crumbs near a log. One ant cannot lift a large crumb. More ants join, and together they carry it home.',
    prompt: 'What is the main idea?',
    choices: [{ id: 'logs', text: 'Ants live only near logs.' }, { id: 'together', text: 'Ants work together to move food.' }, { id: 'crumb-size', text: 'Every crumb is large.' }],
    correctChoiceId: 'together', evidence: 'Several ants join to carry a crumb that one ant cannot lift.',
    hints: ['Think about what most of the passage explains.', 'The ants solve a problem by joining one another.', 'The whole passage shows ants working together.'],
  },
  {
    id: 'crown-grammar', skillId: 'language-mastery-conventions', focus: 'grammar',
    context: 'A sentence needs a complete thought, a capital letter, and an ending mark.',
    prompt: 'Which leaf holds a complete sentence?',
    choices: [{ id: 'fragment', text: 'Under the tall tree.' }, { id: 'complete', text: 'The small owl flies.' }, { id: 'lowercase', text: 'the small owl flies' }],
    correctChoiceId: 'complete', evidence: '“The small owl flies.” names who, tells an action, begins with a capital, and ends with a period.',
    hints: ['Find a choice that tells who and what happened.', 'Check the first letter and the ending mark.', '“The small owl flies.” is a complete thought.'],
  },
  {
    id: 'crown-fluency', skillId: 'reading-mastery-fluency', focus: 'fluency',
    context: 'Read: When the rain stops, we will follow the lanterns home.',
    prompt: 'Which phrase grouping sounds smooth?',
    choices: [{ id: 'word-chop', text: 'When / the rain / stops we / will follow' }, { id: 'smooth', text: 'When the rain stops, / we will follow / the lanterns home.' }, { id: 'single-chop', text: 'When the / rain stops we will / follow the' }],
    correctChoiceId: 'smooth', evidence: 'The smooth groups keep meaningful words together and pause gently at the comma.',
    hints: ['Keep words that belong together in the same phrase.', 'A comma is a natural place for a gentle pause.', '“When the rain stops” makes one complete phrase.'],
  },
];

export function validateGreatStoryTreeTrials(trials = GREAT_STORY_TREE_TRIALS) {
  const expected = new Set(['sequence', 'vocabulary', 'character', 'main-idea', 'grammar', 'fluency']);
  const ids = new Set<string>(); const focuses = new Set<string>();
  trials.forEach(trial => {
    if (ids.has(trial.id)) throw new RangeError(`Duplicate Great Story Tree trial: ${trial.id}`); ids.add(trial.id); focuses.add(trial.focus);
    const choiceIds = trial.choices.map(choice => choice.id);
    if (trial.choices.length < 3 || new Set(choiceIds).size !== choiceIds.length || !choiceIds.includes(trial.correctChoiceId)) throw new RangeError(`Invalid choices for ${trial.id}.`);
    if (!trial.context.trim() || !trial.prompt.trim() || !trial.evidence.trim() || trial.hints.length < 3) throw new RangeError(`Incomplete mastery content for ${trial.id}.`);
  });
  if (trials.length !== expected.size || [...expected].some(focus => !focuses.has(focus))) throw new RangeError('Great Story Tree must cover all six mastery focuses.');
  return true;
}

validateGreatStoryTreeTrials();
